import * as Sentry from '@sentry/react';
import type {
  CreateAdjustmentReasonsAPIRequest,
  CreateAdjustmentReasonsAPIResponse
} from '@library/types/members/memberAdjustments';
import type {
  FormattedResolution,
  GetAllResolutionsQueryParams,
  ResolutionsData,
  TransformedAllResolutionsResponse
} from '@library/types/resolutions/resolutionsList';
import type {
  FormattedResolutionCsvUploadResponse,
  FormattedResolutionDetails,
  FormattedResolutionFailures,
  GetResolutionDetailsAPIRequest,
  GetResolutionFailuresAPIRequest,
  ResolutionCsvUploadResponse,
  ResolutionDetails,
  ResolutionFailuresResponse,
  ResolutionWSData,
  ResolutionsDetails,
  TransformedResolutionFailure,
  TransformedResolutionFailures
} from '@library/types/resolutions/resolutionDetails';
import { apiPaths, memberSupport } from '@bo-constants/services';
import { concat, mergeDeepWith } from 'ramda';
import type { ComboboxOptions } from '@library/components/selects/combobox/Combobox';
import { api } from '@bo-services/api';
import { buildWSUrl } from '@bo-utils/trading';
import { defaultHeaders } from '@bo-constants/apiHeaders';
import { transformAllResolutions } from '@bo-utils/resolutions/allResolutions';
import { transformReasonCode } from '@bo-utils/resolutions/resolutionReasonCode';
import { transformResolutionCsvUploadData } from '@bo-utils/resolutions/resolutionCsvUpload';
import { transformResolutionDetailsData } from '@bo-utils/resolutions/resolutionDetails';
import { transformResolutionFailures } from '@bo-utils/resolutions/resolutionFailures';

const { resolutions } = apiPaths;

export const resolutionsApi = api.injectEndpoints({
  endpoints: (build) => ({
    discardResolution: build.mutation<unknown, { id: string }>({
      query: ({ id, ...body }) => ({
        url: `${resolutions}/${id}/discard`,
        method: 'POST',
        body,
        headers: {
          Accept: 'multipart/form-data'
        }
      }),
      transformErrorResponse: (response) => response.data
    }),
    executeResolution: build.mutation<unknown, { id: string }>({
      query: ({ id, ...body }) => ({
        url: `${resolutions}/${id}/execute`,
        method: 'POST',
        body,
        headers: {
          Accept: 'multipart/form-data'
        }
      }),
      transformErrorResponse: (response) => response.data
    }),
    getAllResolutions: build.query<
      ResolutionsData,
      GetAllResolutionsQueryParams
    >({
      query: ({
        adminIds,
        fromDate,
        page,
        perPage,
        sort,
        statuses,
        toDate
      }) => ({
        url: `${resolutions}`,
        method: 'GET',
        headers: defaultHeaders,
        params: {
          created_at_gteq: fromDate,
          created_at_lteq: toDate,
          created_by_ids: adminIds,
          page,
          perPage,
          sort,
          statuses
        }
      }),
      async onCacheEntryAdded(
        _args,
        { updateCachedData, cacheDataLoaded, cacheEntryRemoved }
      ) {
        try {
          await cacheDataLoaded;

          const wsURL = buildWSUrl();
          const ws = new WebSocket(wsURL || window.backoffice.ws);

          ws.onopen = () => {
            const subscription = JSON.stringify({
              command: 'subscribe',
              identifier: JSON.stringify({
                channel: 'ResolutionsStatusUpdatesChannel'
              })
            });
            ws.send(subscription);
          };

          const listener = (event: MessageEvent) => {
            const eventData = JSON.parse(
              event.data as string
            ) as ResolutionWSData;

            const {
              type,
              message: { data, included } = {
                data: undefined,
                included: undefined
              }
            } = eventData;

            if (type === 'ping' || !data || !included) return;

            updateCachedData((draft) => {
              const updated = transformAllResolutions({
                page: 1,
                perPage: 20,
                response: {
                  data: [data],
                  included
                }
              });

              const updatedResolution = updated.data[0];

              const existingIndex = draft.data.findIndex(
                (res) => res.id === updatedResolution.id
              );

              if (existingIndex !== -1) {
                draft.data[existingIndex] = updatedResolution;
              } else {
                draft.data.unshift(updatedResolution);

                draft.data = draft.data.filter(
                  (res, index, self) =>
                    index === self.findIndex((r) => r.id === res.id)
                );
              }
            });
          };

          ws.addEventListener('message', listener);

          await cacheEntryRemoved;
          ws.removeEventListener('message', listener);
          ws.close();
        } catch (error) {
          Sentry.captureException(error, {
            tags: {
              source: 'resolutionApi.getResolutionDetails WebSocket'
            },
            extra: { message: 'getResolutionDetails WebSocket Error' }
          });
          // eslint-disable-next-line no-console
          console.error('Resolution WebSocket Error', error);
        }
      },
      keepUnusedDataFor: 120,
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        const { page, ...rest } = queryArgs;
        return `${endpointName}${Object.entries(rest).flat(1).join('')}`;
      },
      merge: (currentCache, newItems, { arg }) => {
        if (arg.page === 1) {
          return newItems;
        }

        return mergeDeepWith(
          (a: FormattedResolution, b: FormattedResolution) => {
            if (Array.isArray(a) && Array.isArray(b)) {
              return concat(a, b) as TransformedAllResolutionsResponse;
            }
            return b;
          },
          currentCache,
          newItems
        ) as ResolutionsData;
      },
      forceRefetch: ({ currentArg, previousArg }) =>
        JSON.stringify(currentArg) !== JSON.stringify(previousArg),
      transformResponse: (response: ResolutionsDetails, _, { page, perPage }) =>
        transformAllResolutions({
          page,
          perPage,
          response
        })
    }),
    getResolutionDetails: build.query<
      FormattedResolutionDetails,
      GetResolutionDetailsAPIRequest
    >({
      query: ({ resolutionId }) => ({
        url: `${resolutions}/${resolutionId}`,
        method: 'GET',
        headers: defaultHeaders
      }),
      async onCacheEntryAdded(
        arg,
        { updateCachedData, cacheDataLoaded, cacheEntryRemoved }
      ) {
        try {
          await cacheDataLoaded;

          const wsURL = buildWSUrl();
          const ws = new WebSocket(wsURL || window.backoffice.ws);

          ws.onopen = () => {
            const subscription = JSON.stringify({
              command: 'subscribe',
              identifier: JSON.stringify({
                channel: 'ResolutionsChannel',
                resolution_id: arg.resolutionId
              })
            });
            ws.send(subscription);
          };

          const listener = (event: MessageEvent) => {
            const eventData = JSON.parse(
              event.data as string
            ) as ResolutionWSData;

            const {
              type,
              message: { data, included } = {
                data: undefined,
                included: undefined
              }
            } = eventData;

            if (type === 'ping' || !data || !included) {
              return;
            }

            updateCachedData((draft) => {
              if (eventData.message) {
                const updated = transformResolutionDetailsData({
                  data: eventData.message.data,
                  included: eventData.message.included
                });

                Object.assign(draft, updated);
              }
            });
          };

          ws.addEventListener('message', listener);
          await cacheEntryRemoved;
          ws.removeEventListener('message', listener);
          ws.close();
        } catch (error) {
          Sentry.captureException(error, {
            tags: {
              source: 'resolutionApi.getResolutionDetails WebSocket'
            },
            extra: { message: 'getResolutionDetails Websocket Error' }
          });
          // eslint-disable-next-line no-console
          console.log('Resolution Websocket Error', error);
        }
      },
      keepUnusedDataFor: 120,
      transformResponse: (response: ResolutionDetails) => {
        return transformResolutionDetailsData(response);
      }
    }),
    getResolutionFailures: build.query<
      FormattedResolutionFailures,
      GetResolutionFailuresAPIRequest
    >({
      query: ({ id, page, perPage }) => ({
        url: `${resolutions}/${id}/failures`,
        method: 'GET',
        headers: defaultHeaders,
        params: {
          page,
          per_page: perPage
        }
      }),
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        const { page, ...rest } = queryArgs;
        return `${endpointName}${Object.entries(rest).flat(1).join('')}`;
      },
      merge: (currentCache, newItems, { arg }) => {
        if (arg.page === 1) {
          return newItems;
        }

        return mergeDeepWith(
          (
            a: TransformedResolutionFailure,
            b: TransformedResolutionFailure
          ) => {
            if (Array.isArray(a) && Array.isArray(b)) {
              return concat(a, b) as TransformedResolutionFailures;
            }
            return b;
          },
          currentCache,
          newItems
        ) as FormattedResolutionFailures;
      },
      forceRefetch: ({ currentArg, previousArg }) =>
        JSON.stringify(currentArg) !== JSON.stringify(previousArg),
      transformResponse: (
        response: ResolutionFailuresResponse,
        _,
        { page, perPage }
      ) =>
        transformResolutionFailures({
          page,
          perPage,
          response
        })
    }),

    getResolutionReasonCode: build.query<
      ComboboxOptions,
      CreateAdjustmentReasonsAPIRequest
    >({
      query: ({ reasonType }) => ({
        url: `${memberSupport}reasons`,
        method: 'GET',
        headers: defaultHeaders,
        params: {
          reason_type: reasonType
        }
      }),
      transformResponse: (
        response: CreateAdjustmentReasonsAPIResponse | undefined
      ) => {
        return transformReasonCode({ response });
      }
    }),
    uploadResolutionCSV: build.mutation<
      FormattedResolutionCsvUploadResponse,
      FormData
    >({
      query: (csv) => ({
        url: `${resolutions}`,
        method: 'POST',
        body: csv,
        headers: {
          Accept: 'multipart/form-data'
        }
      }),
      transformResponse: (response: ResolutionCsvUploadResponse) => {
        return transformResolutionCsvUploadData(response);
      },
      transformErrorResponse: (response) => response.data
    })
  })
});

export const {
  useDiscardResolutionMutation,
  useExecuteResolutionMutation,
  useGetAllResolutionsQuery,
  useGetResolutionDetailsQuery,
  useGetResolutionFailuresQuery,
  useGetResolutionReasonCodeQuery,
  useUploadResolutionCSVMutation
} = resolutionsApi;
